import { camelCase } from 'lodash';

import isRecord from 'lib/isRecord';
import { CitationHighlight, parseCitationHighlights, parseCitationPages } from 'models/CitationHighlight';
import { WorkflowAction } from 'models/Workflow';

// Where in the packet the Patient Demographics step read each value, as the api records it beside
// the step's produced values. Keyed by value name (coverages by sequence, then field); each entry
// is one cited document, the same shape every citation badge takes.
export const EXTRACTION_SOURCES_OUTPUT = 'extraction_sources';

export interface ExtractionSource {
  attachmentId: string;
  displayId: number;
  pages?: number[];
  highlights?: CitationHighlight[];
}

export interface ExtractionSources {
  forValue: (name: string) => ExtractionSource[];
  forCoverage: (sequence: string, field: string) => ExtractionSource[];
}

// The response middleware camelCases keys inside a value, so a name written as `patient_dob` reads
// back as `patientDob`. Both spellings are tried, so this keeps working whichever arrives.
const keyed = (record: unknown, name: string): unknown =>
  isRecord(record) ? (record[name] ?? record[camelCase(name)]) : undefined;

function parseSources(value: unknown): ExtractionSource[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const attachmentId = entry.attachmentId ?? entry.attachment_id;
    const displayId = entry.displayId ?? entry.display_id;
    if (typeof attachmentId !== 'string' || typeof displayId !== 'number') return [];
    return [
      {
        attachmentId,
        displayId,
        pages: parseCitationPages(entry.pages),
        highlights: parseCitationHighlights(entry.highlights),
      },
    ];
  });
}

// Absent on a step that ran before sources were recorded, or one whose evidence cited nothing; every
// lookup then comes back empty and no badge renders.
export function extractionSources(action: WorkflowAction): ExtractionSources {
  const raw = action.outputFor(EXTRACTION_SOURCES_OUTPUT);
  return {
    forValue: (name) => parseSources(keyed(raw, name)),
    forCoverage: (sequence, field) => parseSources(keyed(keyed(keyed(raw, 'coverages'), sequence), field)),
  };
}

// Several values' sources as one badge per document: the name is two values read from the same
// lines, and a conflict can span fields. Pages and outlines of the same document are pooled.
export function mergeSources(...lists: ExtractionSource[][]): ExtractionSource[] {
  const byDocument = new Map<string, ExtractionSource>();
  lists.flat().forEach((source) => {
    const seen = byDocument.get(source.attachmentId);
    if (!seen) {
      byDocument.set(source.attachmentId, source);
      return;
    }
    const pages = [...new Set([...(seen.pages ?? []), ...(source.pages ?? [])])].sort((a, b) => a - b);
    const highlights = uniqueInReadingOrder([...(seen.highlights ?? []), ...(source.highlights ?? [])]);
    byDocument.set(source.attachmentId, {
      ...seen,
      pages: pages.length ? pages : undefined,
      highlights: highlights.length ? highlights : undefined,
    });
  });
  return [...byDocument.values()].sort((a, b) => a.displayId - b.displayId);
}

// The same line cited for two values (a first and last name read off one line) is one spot to step
// to, not two. Ordered by page only, and stably: within a page each value's boxes keep the order the
// API sent them in, which keeps a passage continuing up the next column in one piece.
function uniqueInReadingOrder(highlights: CitationHighlight[]): CitationHighlight[] {
  const unique = new Map<string, CitationHighlight>();
  highlights.forEach((h) => {
    const key = `${h.page}:${h.x}:${h.y}:${h.w}:${h.h}`;
    if (!unique.has(key)) unique.set(key, h);
  });
  return [...unique.values()].sort((a, b) => a.page - b.page);
}
